import createCrudService from '@core/api/create-crud-service';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';

import type { TBaseQuery } from '@shared/types/base-query';
import type {
  PROGRAM_STATUSES,
  PROGRAM_TYPES,
  TProgram,
} from '@shared/types/program';
import type { ValueOf } from '@src/shared/types/utilities';
import { useMutation } from '@tanstack/react-query';
import { request } from '@src/core/api/request';
import { queryClient } from '@src/core/api/query-client';
import { showToast } from '@src/shared/utils/helpers/show-toast';

export type { PROGRAM_STATUSES, PROGRAM_TYPES, TProgram };

export type TProgramPayload = {
  name: string;
  description: string;
  country: string;
  logo?: string;
  price?: string;
  start_date?: string;
  deadline?: string;
  status?: ValueOf<typeof PROGRAM_STATUSES>;
  type?: ValueOf<typeof PROGRAM_TYPES>;
  images?: number[];
  is_free?: boolean;
  all_year?: boolean;
};

export type TProgramQuery = {
  is_applied?: boolean;
  is_favorite?: boolean;
  status?: TProgram['status'];
  id?: (number | string)[];
} & TBaseQuery;

const programApi = {
  ...createCrudService<TProgram, TProgramQuery, TProgramPayload>({
    queryKey: [QUERY_KEYS.getPrograms],
    url: 'programs',
  }),

  useDuplicate: () => {
    return useMutation({
      mutationFn: ({ id }: { id: string | number }) =>
        request({
          url: `programs/${id}/duplicate/`,
          method: 'POST',
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === QUERY_KEYS.getPrograms,
        });
        showToast({
          title: 'Duplicate Success',
          description: 'Program has been duplicated successfully',
          variant: 'success',
        });
      },
    });
  },
};

export default programApi;
